import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CountryCode } from "libphonenumber-js";
import apiClient from "../../core/config/api";
import CountryCodeSelect from "./CountryCodeSelect";
import { formatNational, maskedExample, splitPhone, toE164 } from "./phoneUtils";
import "./phone.css";

// SMS code verification of the user's phone number (Bird Verify, sent by the
// backend - app/services/phone_verification.py). Free plan users meet it before
// an instant verification / a results download ("gate"), and when they change
// their number in My Account ("change").

const API = "/account/re/phone";
const CODE_LENGTH = 6;

type ApiError = { response?: { status?: number; data?: { error?: string; message?: string; retry_after?: number } } };

const errorOf = (err: unknown, fallback: string) => {
    const data = (err as ApiError)?.response?.data;
    return { code: data?.error || "", message: data?.message || fallback };
};

interface PhoneVerificationModalProps {
    purpose?: "gate" | "change";
    /** Number to start with (E.164); the pending / stored number when omitted. */
    initialPhone?: string | null;
    /** Send the code as soon as the modal opens (My Account number change). */
    autoSend?: boolean;
    onClose: () => void;
    onVerified: (phone: string) => void;
}

const PhoneVerificationModal = ({ purpose = "gate", initialPhone, autoSend, onClose, onVerified }: PhoneVerificationModalProps) => {
    const start = splitPhone(initialPhone);
    const [country, setCountry] = useState<CountryCode>(start.country);
    const [national, setNational] = useState(start.national);
    const [codeSent, setCodeSent] = useState(false);
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
    const [resendIn, setResendIn] = useState(0);
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const boxes = useRef<(HTMLInputElement | null)[]>([]);
    const autoSent = useRef(false);

    const e164 = toE164(country, national);
    const code = digits.join("");
    const busy = sending || verifying;

    // Resume: a code already on its way to the pending number.
    useEffect(() => {
        if (initialPhone) return;
        let cancelled = false;
        apiClient
            .get(`${API}/status`)
            .then(({ data }) => {
                if (cancelled) return;
                const number = data?.pending_phone_number || data?.phone_number;
                if (number) {
                    const parts = splitPhone(number);
                    setCountry(parts.country);
                    setNational(parts.national);
                }
                if (data?.code_sent && data?.pending_phone_number) {
                    setCodeSent(true);
                    setSentTo(data.pending_phone_number);
                    setResendIn(Number(data.resend_in) || 0);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [initialPhone]);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [resendIn]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !busy) onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [busy, onClose]);

    const sendCode = async (resend = false) => {
        if (busy) return;
        if (!resend && !e164) {
            setError("Enter a valid mobile number.");
            return;
        }
        setSending(true);
        setError(null);
        setNotice(null);
        try {
            const { data } = resend
                ? await apiClient.post(`${API}/resend`)
                : await apiClient.post(`${API}/send`, { phone_number: e164, purpose });
            if (data?.status === "verified") {
                onVerified(data.phone_number || e164 || "");
                return;
            }
            setCodeSent(true);
            setSentTo(data?.phone_number || e164);
            setResendIn(Number(data?.resend_in) || 60);
            setDigits(Array(CODE_LENGTH).fill(""));
            setNotice(resend ? "A new code is on its way." : null);
            window.setTimeout(() => boxes.current[0]?.focus(), 0);
        } catch (err) {
            const { message } = errorOf(err, "We couldn't send the code. Please try again.");
            const retry = (err as ApiError)?.response?.data?.retry_after;
            if (retry && retry < 120) setResendIn(retry);
            setError(message);
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (autoSend && initialPhone && !autoSent.current) {
            autoSent.current = true;
            sendCode();
        }
    }, [autoSend, initialPhone]); // eslint-disable-line react-hooks/exhaustive-deps

    const verify = async () => {
        if (busy || code.length !== CODE_LENGTH) return;
        setVerifying(true);
        setError(null);
        setNotice(null);
        try {
            const { data } = await apiClient.post(`${API}/verify`, { code });
            onVerified(data?.phone_number || sentTo || e164 || "");
        } catch (err) {
            const { code: reason, message } = errorOf(err, "We couldn't verify that code. Please try again.");
            setError(message);
            if (reason === "code_expired" || reason === "attempts_exhausted") setResendIn(0);
            setDigits(Array(CODE_LENGTH).fill(""));
            window.setTimeout(() => boxes.current[0]?.focus(), 0);
        } finally {
            setVerifying(false);
        }
    };

    const setDigit = (index: number, value: string) => {
        const clean = value.replace(/\D/g, "");
        if (clean.length > 1) {
            // Pasted code: spread it over the boxes.
            const next = clean.slice(0, CODE_LENGTH).split("");
            setDigits(Array.from({ length: CODE_LENGTH }, (_, i) => next[i] || ""));
            boxes.current[Math.min(next.length, CODE_LENGTH) - 1]?.focus();
            return;
        }
        setDigits((prev) => prev.map((d, i) => (i === index ? clean : d)));
        if (clean && index < CODE_LENGTH - 1) boxes.current[index + 1]?.focus();
    };

    const onBoxKey = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !digits[index] && index > 0) boxes.current[index - 1]?.focus();
        if (e.key === "Enter") verify();
    };

    // A different number from the one the code went to needs a new code.
    const numberChanged = codeSent && !!e164 && e164 !== sentTo;

    return createPortal(
        <div className="pv-overlay" onClick={() => !busy && onClose()}>
            <div
                className="pv-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pv-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="pv-header">
                    <h2 id="pv-title">
                        {purpose === "change" ? "Verify your new phone number" : "Verify your phone number to unlock results"}
                    </h2>
                    <button type="button" className="pv-close" aria-label="Close" onClick={onClose} disabled={busy}>
                        &times;
                    </button>
                </div>
                <p className="pv-subtitle">
                    {purpose === "change"
                        ? "Enter the code we sent by SMS. Your current number stays active until the new one is verified."
                        : "Complete verification to view the email verification result and get 100 free credits."}
                </p>

                <div className="pv-card">
                    <div className="pv-row">
                        <label htmlFor="pv-number">Mobile Number*</label>
                        <div className="pv-number">
                            <CountryCodeSelect value={country} onChange={setCountry} disabled={busy} />
                            <div className="pv-number-field">
                                <input
                                    id="pv-number"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel-national"
                                    placeholder={maskedExample(country)}
                                    value={formatNational(country, national)}
                                    onChange={(e) => setNational(e.target.value.replace(/\D/g, "").slice(0, 15))}
                                    onKeyDown={(e) => e.key === "Enter" && sendCode()}
                                    disabled={busy}
                                />
                                <button
                                    type="button"
                                    className="pv-send"
                                    onClick={() => sendCode()}
                                    disabled={busy || !e164 || (codeSent && !numberChanged && resendIn > 0)}
                                    aria-label="Send code"
                                    title={codeSent && !numberChanged ? "Code sent" : "Send code"}
                                >
                                    {sending ? (
                                        <span className="pv-spinner" />
                                    ) : (
                                        <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
                                            <path d="M15 1 7.5 8.5M15 1l-5 14-2.5-6.5L1 6l14-5Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="pv-row pv-row-code">
                        <label>Enter the 6-digit code</label>
                        <div className="pv-code-wrap">
                            <div className="pv-code" role="group" aria-label="Verification code">
                                {digits.map((d, i) => (
                                    <input
                                        key={i}
                                        ref={(el) => {
                                            boxes.current[i] = el;
                                        }}
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete={i === 0 ? "one-time-code" : "off"}
                                        maxLength={CODE_LENGTH}
                                        value={d}
                                        onChange={(e) => setDigit(i, e.target.value)}
                                        onKeyDown={(e) => onBoxKey(i, e)}
                                        disabled={!codeSent || busy}
                                        aria-label={`Digit ${i + 1}`}
                                    />
                                ))}
                            </div>
                            <button
                                type="button"
                                className="pv-resend"
                                onClick={() => sendCode(true)}
                                disabled={!codeSent || busy || resendIn > 0 || numberChanged}
                            >
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                                </svg>
                                {resendIn > 0 && codeSent ? `Resend OTP in ${resendIn}s` : "Resend OTP"}
                            </button>
                        </div>
                    </div>

                    {(error || notice || (codeSent && sentTo)) && (
                        <p className={`pv-message${error ? " is-error" : ""}`} role={error ? "alert" : "status"}>
                            {error || notice || `Code sent to ${sentTo}.`}
                        </p>
                    )}
                </div>

                <button
                    type="button"
                    className="pv-submit"
                    onClick={verify}
                    disabled={!codeSent || code.length !== CODE_LENGTH || busy || numberChanged}
                >
                    {verifying ? "Verifying..." : purpose === "change" ? "Verify Number" : "Verify & Unlock 100 Credits"}
                </button>
            </div>
        </div>,
        document.body
    );
};

export default PhoneVerificationModal;
